/**
 * Plugin component registry with dual-mode loading.
 *
 * Dev mode (import.meta.env.PROD === false): Local code-split registry
 * using React.lazy() — avoids Vite's dep pre-bundle hash instability.
 *
 * Production mode (import.meta.env.PROD === true): Import Maps are the
 * primary mechanism. The verisa-import-map Vite plugin generates a
 * <script type="importmap"> mapping shared dependencies to deterministic
 * hashed chunk URLs. Plugin bundles externalize shared deps as bare
 * import specifiers resolved by the browser-native import map.
 *
 * @see HLD-015 §11.2, §11.3
 */
import React from "react";

export type ComponentMap = Record<string, React.ComponentType<any>>;
export type PluginComponentMap = ComponentMap;

/**
 * Dev-mode local code-split registry.
 * In production, resolvePluginComponent resolves components via Import Maps
 * instead (HLD-015 §11.2). This registry is the fallback for dev mode.
 *
 * IMPORTANT: Factories are called lazily and cached in pluginComponentCache
 * so that React.lazy() instances have stable identities across renders.
 * Without caching, every render creates new lazy component types which
 * prevents React startTransition from ever settling (see v7_startTransition).
 */
const pluginRegistry: Record<string, () => ComponentMap> = {
  "agentic-research": () => ({
    main: React.lazy(() => import("./agentic-research/ResearchPage")),
    "workspace:main": React.lazy(() => import("./agentic-research/ResearchPage")),
  }),
  "translator": () => ({
    main: React.lazy(() => import("./translator/Translator")),
    "workspace:main": React.lazy(() => import("./translator/Translator")),
  }),
  "doc-processor": () => ({
    main: React.lazy(() => import("./doc-processor/pages/DocumentProcessorPage")),
    "workspace:main": React.lazy(() => import("./doc-processor/pages/WorkspaceDocumentProcessorPage")),
    "page:report-history": React.lazy(() => import("./doc-processor/pages/ReportHistoryPage")),
    // The former page:document-manager key was removed (no consumer): the parked
    // DocumentManagerPage is routed as a CORE route in web/src/apps/tenant/routes.tsx,
    // release-gated OFF behind the fail-closed `agent.documents` feature key (D5,
    // D-022-backed; build-vs-delete pending the W5 DECISIONS.md ruling —
    // TASK-BC-PARKED-SURFACES-GATE). Keeping the slot would leave a registry
    // side-door to a surface the route gate closed.
    "settings:processor": React.lazy(() => import("./doc-processor/pages/SettingsPage")),
  }),
  "web-crawler": () => ({
    // The source-config pages (list/create/detail) are routed as CORE routes via
    // direct lazy import in web/src/apps/tenant/routes.tsx — NOT resolved through
    // this registry — so the former page:crawl-source(s)/-create/-detail and
    // page:workspace-sources keys were removed (no consumer). `main` (the plugin's
    // default surface) + settings:crawler remain registry-resolved.
    main: React.lazy(() => import("./web-crawler/pages/SourcesWebsite")),
    "settings:crawler": React.lazy(() => import("./web-crawler/pages/CrawlerSettingsPage")),
  }),
  "rag-chatbot": () => ({
    main: React.lazy(() => import("./rag-chatbot/pages/ChatPlayground")),
    "settings:knowledge": React.lazy(() => import("./rag-chatbot/components/KnowledgeSettings")),
    // TODO: Add as pages are moved into the plugin
    // "page:chat-logs": React.lazy(() => import("./rag-chatbot/pages/ChatLogs")),
    // "page:ab-testing": React.lazy(() => import("./rag-chatbot/pages/ABTesting")),
    // "page:training": React.lazy(() => import("./rag-chatbot/pages/Training")),
    // "page:schedules": React.lazy(() => import("./rag-chatbot/pages/Schedules")),
    // "page:job-history": React.lazy(() => import("./rag-chatbot/pages/JobHistory")),
    // "settings:ai": React.lazy(() => import("./rag-chatbot/pages/AISettings")),
  }),
  "artis": () => ({
    main: React.lazy(() => import("./rag-chatbot/pages/ChatPlayground")),
    "settings:artis": React.lazy(() => import("./artis/pages/ArtisSettingsPage")),
  }),
  // Adatkinyerő (bulk, schema-driven data extraction). One root component serves both
  // SPAs; every view, tab and panel is addressed by search params (?view=...&item=...),
  // see plugins/adatkinyero/app/routing.ts, because AgentPage and the tenant playground
  // route accept no plugin sub-paths. The key equals the manifest plugin.name.
  "adatkinyero": () => ({
    main: React.lazy(() => import("./adatkinyero/pages/AdatkinyeroAdminRoot")),
    "workspace:main": React.lazy(() => import("./adatkinyero/pages/AdatkinyeroWorkspaceRoot")),
  }),
};

/** Cache of resolved component maps — ensures React.lazy identities are stable. */
const pluginComponentCache: Record<string, ComponentMap> = {};

/**
 * Resolve a plugin component by plugin name and component key.
 * Always checks the local code-split registry first (works in both dev and prod
 * for plugins bundled with the main app). Falls back to Import Map resolution
 * in production for externally-built plugins (HLD-015 §11.2).
 */
export function resolvePluginComponent(
  pluginName: string,
  componentKey: string = "main",
): React.ComponentType<any> | null {
  // Always try local registry first — plugins bundled with the main app
  // (rag-chatbot, web-crawler, doc-processor) are registered here.
  if (!pluginComponentCache[pluginName]) {
    const factory = pluginRegistry[pluginName];
    if (factory) {
      pluginComponentCache[pluginName] = factory();
    }
  }
  const cached = pluginComponentCache[pluginName];
  if (cached) {
    return cached[componentKey] ?? null;
  }

  // Production-only: Import Map fallback for externally-built plugins
  // that are NOT in the local registry.
  if (import.meta.env.PROD) {
    const LazyComponent = React.lazy(
      () => import(/* @vite-ignore */ `@verisa/plugin-${pluginName}`)
        .then(mod => {
          const components = mod.register();
          const Component = components[componentKey];
          if (!Component) throw new Error(`Plugin ${pluginName} has no component "${componentKey}"`);
          return { default: Component };
        })
        .catch((err) => {
          console.error(`[plugins/registry] Failed to load plugin "${pluginName}" component "${componentKey}":`, err);
          return { default: () => null };
        })
    );
    return LazyComponent;
  }

  return null;
}
